// pages/Bills.jsx - Complete implementation
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_PATH from '../apiConfig';
import AddBillModal from '../components/AddBillModal';
import BillPaymentModal from '../components/BillPaymentModal';
import { formatCurrency, formatLocalDate } from '../utils';
import { showToast } from '../components/Toast';

const Bills = () => {
  const navigate = useNavigate();
  const [bills, setBills] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [billPayments, setBillPayments] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [editingBill, setEditingBill] = useState(null);
  const [selectedBill, setSelectedBill] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'next_due_date', direction: 'asc' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const currentMonth = new Date().toISOString().substring(0, 7);
      const [billsResponse, budgetsResponse] = await Promise.all([
        axios.get(`${API_BASE_PATH}/bills`),
        axios.get(`${API_BASE_PATH}/budget-items`, { params: { month: currentMonth } })
      ]);

      const billsData = billsResponse.data || [];
      setBills(billsData);

      // Filter and deduplicate budgets
      const budgetItems = (budgetsResponse.data || []).filter(b => b.type === 'budget');
      const uniqueBudgets = budgetItems.filter((budget, index, self) =>
        index === self.findIndex((b) => b.id === budget.id)
      );
      setBudgets(uniqueBudgets);

      // Fetch all transactions to check payment status for each bill
      try {
        const transactionsResponse = await axios.get(`${API_BASE_PATH}/transactions`);
        const allTransactions = transactionsResponse.data || [];

        // Check payment status for each bill
        const paymentsData = {};
        billsData.forEach((bill) => {
          // Find transactions for this bill
          const billTransactions = allTransactions.filter(t => t.bill_id === bill.id);

          if (billTransactions.length > 0 && bill.next_due_date) {
            // Sort by date descending and get the most recent
            const sorted = [...billTransactions].sort((a, b) =>
              new Date(b.date).getTime() - new Date(a.date).getTime()
            );
            const lastPayment = sorted[0];
            const lastPaymentDate = new Date(lastPayment.date);
            const nextDueDate = new Date(bill.next_due_date);

            // Calculate previous due date based on frequency
            const prevDueDate = new Date(nextDueDate);
            switch (bill.frequency) {
              case 'weekly':
                prevDueDate.setDate(prevDueDate.getDate() - 7);
                break;
              case 'biweekly':
                prevDueDate.setDate(prevDueDate.getDate() - 14);
                break;
              case 'monthly':
                prevDueDate.setMonth(prevDueDate.getMonth() - 1);
                break;
              case 'quarterly':
                prevDueDate.setMonth(prevDueDate.getMonth() - 3);
                break;
              case 'annually':
                prevDueDate.setFullYear(prevDueDate.getFullYear() - 1);
                break;
            }

            // If payment was made after the previous due date, it's for current cycle
            paymentsData[bill.id] = lastPaymentDate >= prevDueDate;
          }
        });

        setBillPayments(paymentsData);
      } catch (err) {
        console.error('Error fetching transactions for bill status:', err);
      }
    } catch (error) {
      console.error('Error fetching bills:', error);
      showToast('Failed to load bills', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddBill = () => {
    setEditingBill(null);
    setShowAddModal(true);
  };

  const handleEditBill = (bill) => {
    setEditingBill(bill);
    setShowAddModal(true);
  };

  const handleDeleteBill = async (billId) => {
    if (!confirm('Are you sure you want to delete this bill?')) return;
    
    try {
      await axios.delete(`${API_BASE_PATH}/bills/${billId}`);
      showToast('Bill deleted', 'success');
      fetchData();
    } catch (error) {
      console.error('Error deleting bill:', error);
      showToast('Failed to delete bill', 'error');
    }
  };

  const handlePayBill = (bill) => {
    setSelectedBill(bill);
    setShowPaymentModal(true);
  };

  const summary = bills.reduce((acc, bill) => {
    acc.totalAmount += bill.amount;
    acc.billCount++;

    if (bill.next_due_date) {
      const daysUntilDue = Math.ceil((new Date(bill.next_due_date) - new Date()) / (1000 * 60 * 60 * 24));
      if (daysUntilDue < 0) {
        acc.overdue++;
      } else if (daysUntilDue <= 7) {
        acc.dueSoon++;
      }
    }

    if (bill.auto_pay) {
      acc.autoPay++;
    }

    if (billPayments[bill.id]) {
      acc.paid++;
    }

    return acc;
  }, { totalAmount: 0, billCount: 0, overdue: 0, dueSoon: 0, autoPay: 0, paid: 0 });

  const sortBills = (bills) => {
    if (!sortConfig.key) return bills;

    return [...bills].sort((a, b) => {
      let aVal, bVal;

      switch (sortConfig.key) {
        case 'name':
          aVal = a.name.toLowerCase();
          bVal = b.name.toLowerCase();
          break;
        case 'amount':
          aVal = a.amount;
          bVal = b.amount;
          break;
        case 'budget':
          const aBudget = budgets.find(b => b.id === a.budget_id);
          const bBudget = budgets.find(b => b.id === b.budget_id);
          aVal = aBudget ? aBudget.name.toLowerCase() : '';
          bVal = bBudget ? bBudget.name.toLowerCase() : '';
          break;
        case 'next_due_date':
          aVal = a.next_due_date ? new Date(a.next_due_date).getTime() : 9999999999999;
          bVal = b.next_due_date ? new Date(b.next_due_date).getTime() : 9999999999999;
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const SortableHeader = ({ sortKey, label }) => {
    const isActive = sortConfig.key === sortKey;
    
    return (
      <th 
        onClick={() => handleSort(sortKey)}
        style={{ cursor: 'pointer', userSelect: 'none' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          {label}
          <i 
            className={`fas fa-sort${isActive ? (sortConfig.direction === 'asc' ? '-up' : '-down') : ''}`}
            style={{ 
              fontSize: '11px',
              opacity: isActive ? 1 : 0.3,
              color: isActive ? 'var(--primary-color)' : 'inherit'
            }}
          ></i>
        </div>
      </th>
    );
  };

  const sortedBills = sortBills(bills);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border text-primary"></div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ 
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div>
          <h1 style={{ fontSize: '26px', fontWeight: 700, margin: 0 }}>
            <i className="fas fa-file-invoice" style={{ marginRight: '10px' }}></i>
            Bills
          </h1>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '4px 0 0 0' }}>
            Manage recurring bills and track payments
          </p>
        </div>
        <button className="btn btn-primary" onClick={handleAddBill}>
          <i className="fas fa-plus"></i>
          Add Bill
        </button>
      </div>

      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', 
        gap: '16px',
        marginBottom: '24px'
      }}>
        <div className="metric-card">
          <div className="metric-label">
            <i className="fas fa-receipt"></i>
            Total Monthly Bills
          </div>
          <div className="metric-value">
            {formatCurrency(summary.totalAmount)}
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
            {summary.billCount} recurring {summary.billCount === 1 ? 'bill' : 'bills'}
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-label">
            <i className="fas fa-exclamation-triangle"></i>
            Due Soon
          </div>
          <div className="metric-value" style={{ color: summary.dueSoon > 0 ? 'var(--warning-color)' : 'var(--success-color)' }}>
            {summary.dueSoon}
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
            Due within 7 days
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-label">
            <i className="fas fa-clock"></i>
            Overdue
          </div>
          <div className="metric-value" style={{ color: summary.overdue > 0 ? 'var(--danger-color)' : 'var(--success-color)' }}>
            {summary.overdue}
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
            Past due date
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-label">
            <i className="fas fa-check-circle"></i>
            Paid This Cycle
          </div>
          <div className="metric-value" style={{ color: summary.paid > 0 ? 'var(--success-color)' : 'var(--text-primary)' }}>
            {summary.paid}
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
            of {summary.billCount} bills paid
          </div>
        </div>
      </div>

      {bills.length === 0 ? (
        <div className="empty-state">
          <i className="fas fa-file-invoice empty-state-icon"></i>
          <h3 className="empty-state-title">No bills tracked</h3>
          <p className="empty-state-description">
            Add your first recurring bill to start tracking payments
          </p>
          <button className="btn btn-primary" onClick={handleAddBill} style={{ marginTop: '16px' }}>
            <i className="fas fa-plus"></i>
            Add Your First Bill
          </button>
        </div>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <SortableHeader sortKey="name" label="Bill Name" />
                <SortableHeader sortKey="budget" label="Budget" />
                <SortableHeader sortKey="amount" label="Amount" />
                <SortableHeader sortKey="next_due_date" label="Next Due" />
                <th>Frequency</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedBills.map(bill => {
                const budget = budgets.find(b => b.id === bill.budget_id);
                const daysUntilDue = bill.next_due_date
                  ? Math.ceil((new Date(bill.next_due_date) - new Date()) / (1000 * 60 * 60 * 24))
                  : null;
                const isPaid = billPayments[bill.id] || false;

                return (
                  <tr
                    key={bill.id}
                    onClick={() => navigate(`/bills/${bill.id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <strong>{bill.name}</strong>
                        {isPaid && (
                          <span
                            className="badge-success"
                            style={{
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '10px',
                              fontWeight: 600,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                            title="Paid for current period"
                          >
                            <i className="fas fa-check-circle"></i>
                            PAID
                          </span>
                        )}
                        {bill.auto_pay && (
                          <i
                            className="fas fa-robot"
                            style={{ color: 'var(--primary-color)', fontSize: '12px' }}
                            title="Auto-pay enabled"
                          ></i>
                        )}
                      </div>
                      {bill.notes && (
                        <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                          {bill.notes}
                        </div>
                      )}
                    </td>
                    <td>
                      {budget ? (
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '6px',
                          fontSize: '11px',
                          fontWeight: 600,
                          background: 'var(--bg-tertiary)',
                          color: 'var(--text-primary)'
                        }}>
                          {budget.name}
                        </span>
                      ) : (
                        <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                          Not assigned
                        </span>
                      )}
                    </td>
                    <td>
                      <strong>{formatCurrency(bill.amount)}</strong>
                    </td>
                    <td>
                      {bill.next_due_date ? (
                        <>
                          {formatLocalDate(bill.next_due_date)}
                          {daysUntilDue !== null && (
                            <div style={{ 
                              fontSize: '11px', 
                              color: daysUntilDue < 0 ? 'var(--danger-color)' : 
                                     daysUntilDue < 7 ? 'var(--warning-color)' : 
                                     'var(--text-secondary)'
                            }}>
                              {daysUntilDue < 0 ? `${Math.abs(daysUntilDue)} days overdue` : 
                               daysUntilDue === 0 ? 'Due today' : 
                               `in ${daysUntilDue} days`}
                            </div>
                          )}
                        </>
                      ) : '-'}
                    </td>
                    <td>
                      <span style={{ textTransform: 'capitalize' }}>
                        {bill.frequency || 'monthly'}
                      </span>
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          className="btn-table-action"
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePayBill(bill);
                          }}
                          title="Record payment"
                          style={{ color: 'var(--success-color)' }}
                        >
                          <i className="fas fa-dollar-sign"></i>
                        </button>
                        <button
                          className="btn-table-action"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditBill(bill);
                          }}
                          title="Edit bill"
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                        <button
                          className="btn-table-action danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteBill(bill.id);
                          }}
                          title="Delete bill"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showAddModal && (
        <AddBillModal
          item={editingBill}
          budgets={budgets}
          onClose={() => {
            setShowAddModal(false);
            setEditingBill(null);
          }}
          onSave={() => {
            setShowAddModal(false);
            setEditingBill(null);
            fetchData();
          }}
        />
      )}

      {showPaymentModal && selectedBill && (
        <BillPaymentModal
          bill={selectedBill}
          onClose={() => {
            setShowPaymentModal(false);
            setSelectedBill(null);
          }}
          onSave={() => {
            setShowPaymentModal(false);
            setSelectedBill(null);
            fetchData();
          }}
        />
      )}
    </div>
  );
};

export default Bills;